/**
 * Companion element for the Shopify Subscriptions app block.
 *
 * Self-contained (no theme imports) so it can't fail on module resolution.
 * When a subscription (selling plan) is selected it drives the existing
 * <quantity-selector-component> to the current variant's bulk pack count, and
 * restores the original quantity on one-time purchase.
 */
(function () {
  'use strict';

  class SubscriptionBulkPack extends HTMLElement {
    connectedCallback() {
      try {
        this.counts = JSON.parse(this.dataset.counts || '{}');
      } catch (e) {
        this.counts = {};
      }
      try {
        this.qtys = JSON.parse(this.dataset.qtys || '{}');
      } catch (e) {
        this.qtys = {};
      }
      this.behavior = this.dataset.behavior || 'lock';
      this.active = false;
      this.applied = false;
      this.saved = null;
      this.currentVariantId = this.readVariantId();

      this.onChange = this.onChange.bind(this);
      this.onVariantUpdate = this.onVariantUpdate.bind(this);

      document.addEventListener('change', this.onChange, true);
      document.addEventListener('variant:update', this.onVariantUpdate);

      this.evaluate();
      this.t1 = setTimeout(() => this.evaluate(), 300);
      this.t2 = setTimeout(() => this.evaluate(), 1200);
      this.poll = setInterval(() => this.evaluate(), 1000);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange, true);
      document.removeEventListener('variant:update', this.onVariantUpdate);
      clearTimeout(this.t1);
      clearTimeout(this.t2);
      clearInterval(this.poll);
    }

    section() {
      return this.closest('.shopify-section, dialog');
    }

    form() {
      const scope = this.section() || document;
      return scope.querySelector('product-form-component') || document.querySelector('product-form-component');
    }

    quantitySelector() {
      const form = this.form();
      return (form && form.querySelector('quantity-selector-component')) || null;
    }

    quantityInput(qs) {
      if (!qs) return null;
      return (
        (qs.refs && qs.refs.quantityInput) ||
        qs.querySelector('input[name="quantity"]') ||
        qs.querySelector('input')
      );
    }

    readVariantId() {
      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl) return fromUrl;
      const form = this.form();
      const input = form && form.querySelector('input[name="id"]');
      return (input && input.value) || null;
    }

    sellingPlanState() {
      const roots = [this.section(), document].filter(Boolean);
      for (const root of roots) {
        const radio = root.querySelector('input[name="selling_plan"]:checked');
        if (radio) return { found: true, value: radio.value || '' };
        const select = root.querySelector('select[name="selling_plan"]');
        if (select) return { found: true, value: select.value || '' };
        const hidden = root.querySelector('input[name="selling_plan"]');
        if (hidden) return { found: true, value: hidden.value || '' };
      }
      return { found: false, value: '' };
    }

    onChange(event) {
      const target = event.target;
      if (target && target.name === 'selling_plan') this.evaluate();
    }

    onVariantUpdate(event) {
      const id = event && event.detail && event.detail.resource && event.detail.resource.id;
      this.currentVariantId = id ? String(id) : this.readVariantId();
      this.saved = null;
      this.applied = false;
      if (this.active) {
        this.apply();
        // Re-apply after the theme finishes morphing the quantity selector.
        setTimeout(() => this.active && this.apply(), 50);
        setTimeout(() => this.active && this.apply(), 350);
      }
    }

    evaluate() {
      const state = this.sellingPlanState();
      const subscription = Boolean(state.value);

      if (subscription && !this.active) {
        this.active = true;
        this.applied = false;
        this.apply();
      } else if (!subscription && this.active) {
        this.active = false;
        this.applied = false;
        this.restore();
      } else if (subscription && this.active) {
        // Lock re-applies continuously (value is fixed anyway); the other modes
        // apply once so the customer's edits are preserved.
        if (this.behavior === 'lock' || !this.applied) this.apply();
      }
    }

    getCount() {
      if (!this.currentVariantId) this.currentVariantId = this.readVariantId();
      return this.currentVariantId ? this.counts[this.currentVariantId] : undefined;
    }

    getQty() {
      if (!this.currentVariantId) this.currentVariantId = this.readVariantId();
      return this.currentVariantId ? this.qtys[this.currentVariantId] : undefined;
    }

    apply() {
      const qs = this.quantitySelector();
      const count = this.getCount();
      const input = this.quantityInput(qs);
      if (!qs || !count || !input) {
        this.toggleNote(false);
        return;
      }

      if (!this.saved) {
        this.saved = {
          min: input.min || '1',
          max: input.getAttribute('max'),
          step: input.step || '1',
          value: input.value,
        };
      }

      const countStr = String(count);

      if (this.behavior === 'lock') {
        if (qs.updateConstraints) qs.updateConstraints(countStr, countStr, countStr);
        if (qs.setValue) qs.setValue(countStr);
        input.value = countStr;
        this.setLocked(qs, true);
      } else if (this.behavior === 'min') {
        if (qs.updateConstraints) qs.updateConstraints(countStr, this.saved.max, countStr);
        const current = parseInt(input.value, 10) || count;
        const snapped = Math.max(count, Math.ceil(current / count) * count);
        if (qs.setValue) qs.setValue(String(snapped));
        input.value = String(snapped);
      } else {
        if (qs.setValue) qs.setValue(countStr);
        input.value = countStr;
      }

      if (qs.updateButtonStates) qs.updateButtonStates();
      this.applied = true;
      this.toggleNote(true, count);
    }

    restore() {
      const qs = this.quantitySelector();
      this.toggleNote(false);
      if (!qs || !this.saved) {
        this.setLocked(qs, false);
        return;
      }
      const input = this.quantityInput(qs);
      this.setLocked(qs, false);
      if (qs.updateConstraints) qs.updateConstraints(this.saved.min, this.saved.max, this.saved.step);
      const restoreValue = this.saved.min || '1';
      if (qs.setValue) qs.setValue(restoreValue);
      if (input) input.value = restoreValue;
      if (qs.updateButtonStates) qs.updateButtonStates();
      this.saved = null;
    }

    setLocked(qs, locked) {
      if (!qs) return;
      const input = this.quantityInput(qs);
      const minus = qs.refs && qs.refs.minusButton;
      const plus = qs.refs && qs.refs.plusButton;
      if (input) {
        input.readOnly = locked;
        input.classList.toggle('is-locked', locked);
      }
      if (minus) minus.disabled = locked;
      if (plus) plus.disabled = locked;
      qs.classList.toggle('quantity-selector--locked', locked);
    }

    toggleNote(show, count) {
      const note = this.querySelector('[ref="note"]');
      if (!note) return;
      note.hidden = !show;
      if (show && count != null) {
        const template = note.dataset.template || '';
        if (template) {
          const qty = this.getQty();
          //let text = template.replace('[qty]', String(count));
          let text = template.replace('[count]', String(count));
          if (qty != null) {
            text = text.replace('[qty]', String(qty));
          } else {
            // Variant saknar bulk-qty: städa bort " á [qty]" ur texten
            text = text.replace(/\s*á\s*\[qty\]/, '').replace('[qty]', '');
          }
          note.textContent = text;
        }
      }
    }
  }

  if (!customElements.get('subscription-bulk-pack')) {
    customElements.define('subscription-bulk-pack', SubscriptionBulkPack);
  }
})();